import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Prompt, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { FigureVersion, StoriesIncutBlock, IncutStatus, StoryStatus } from '@graphql/types';
import { useStore } from '@stores/rootStoreContext';
import SimpleModal from '@components/UI/SimpleModal/SimpleModal';
import { grey50 } from '@theme/vars';
import StoryIncutGridDrop from '../StoryIncutGridDrop/StoryIncutGridDrop';
import MaterialIncutUrl from '@components/StoryIncuts/MaterialIncutUrl/MaterialIncutUrl';
import ButtonForInput from '../../UI/styled/ButtonForInput';
import {
    usePublicationByUrlForBannerQuery,
    useSaveStoriesIncutBlockMutation,
    useStoriesIncutSelectionLazyQuery,
    useUpdateStoriesIncutSelectionMutation,
} from './gql/StoryIncutContent.generated';
import { TOP_INCUT_CONTENT_CONSTS } from './StoryIncutContent.consts';
import { Header, HistoryAndSaveBox, ModalDescription, Wrapper } from './StoryIncutContent.styles';

interface StoryIncutContentProps {
    storyId: string;
    storyStatus: StoryStatus;
    onIncutSave: () => Promise<void>;
    setTabValue?: (value: number) => void; // Новый проп для изменения вкладки
}

const StoryIncutContent: FC<StoryIncutContentProps> = ({ storyId, storyStatus, onIncutSave, setTabValue }) => {
    const [url, setUrl] = useState('');
    const { storiesIncutStore } = useStore();
    const {
        addingBlocksCache,
        deletingBlocksCache,
        storiesIncutBlocksUids,
        confirmSaveModalOpen,
        resetCache,
        setConfirmSaveModalOpen,
        setStoriesIncutBlocksUids,
        incutCursor,
    } = storiesIncutStore;
    const [storiesIncutBlocks, setStoriesIncutBlocks] = useState<StoriesIncutBlock[]>([]);
    const [blocksChanged, setBlocksChanged] = useState(false);
    const [isSwapItems, setIsSwapItems] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [figureVersion, setFigureVersion] = useState({} as FigureVersion);
    const [currentPublication, setCurrentPublication] = useState({} as any);
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [updateStoriesIncutSelection] = useUpdateStoriesIncutSelectionMutation();
    const [saveStoriesIncutBlock] = useSaveStoriesIncutBlockMutation();
    const [isActiveBtn, setIsActiveBtn] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [nextLocation, setNextLocation] = useState<string | null>(null);
    const [nextTab, setNextTab] = useState<number | null>(null); // Состояние для целевой вкладки

    const isReadOnly = storyStatus === StoryStatus.Deleted;

    useEffect(() => {
        setStoriesIncutBlocks(storiesIncutStore.storiesIncutBlocks);
    }, [storiesIncutStore.storiesIncutBlocks]);

    usePublicationByUrlForBannerQuery({
        variables: { url },
        onCompleted: (data) => {
            if (url) {
                setCurrentPublication(data.publicationByUrl);
                if (data?.publicationByUrl?.__typename === 'Publication') {
                    setFigureVersion({
                        crop: data.publicationByUrl.announceImage?.figureVersion?.crop,
                        figure: data.publicationByUrl.announceImage?.figureVersion?.figure,
                        hasDiagonalWatermark: false,
                        hasWatermark: false,
                        id: data.publicationByUrl.announceImage?.figureVersion?.id,
                        primaryProcessedImage: data.publicationByUrl.announceImage?.figureVersion?.primaryProcessedImage,
                        uid: data.publicationByUrl.announceImage?.figureVersion?.uid,
                    } as FigureVersion);
                }
                setDataLoaded(true);
            }
        },
    });

    const handleUpdateStoriesIncutSelection = useCallback(async () => {
        try {
            if (isValid) {
                await updateStoriesIncutSelection({
                    variables: {
                        data: {
                            storyId,
                            blockIds: storiesIncutBlocks.map((block) => block.uid),
                            endIncutCursor: incutCursor,
                        },
                    },
                });

                setStoriesIncutBlocksUids([]);
                resetCache();
                await onIncutSave();
                enqueueSnackbar(TOP_INCUT_CONTENT_CONSTS.savedMsg, { variant: 'success' });
                setBlocksChanged(false);
                setIsSwapItems(false);
                setIsActiveBtn(false);
            }
        } catch (e) {
            console.error(e);
        }
    }, [
        isValid,
        storyId,
        enqueueSnackbar,
        storiesIncutBlocks,
        storiesIncutBlocksUids,
        resetCache,
        setStoriesIncutBlocksUids,
        updateStoriesIncutSelection,
        onIncutSave,
        incutCursor,
    ]);

    const handleCreateBlockModal = useCallback(async () => {
        try {
            const saveStoriesIncutBlockData = await saveStoriesIncutBlock({
                variables: {
                    data: {
                        id: null,
                        url,
                        title: currentPublication?.title,
                        incutType: null,
                        figureVersionId: figureVersion?.id,
                    },
                },
            });

            const updatedBlock = {
                ...saveStoriesIncutBlockData.data?.saveStoriesIncutBlock?.block,
                position: 0,
            };

            if (!saveStoriesIncutBlockData.errors?.length) {
                const newStoriesIncutBlocksUids = [...(storiesIncutBlocksUids || [])];
                newStoriesIncutBlocksUids.unshift(updatedBlock?.uid);
                setStoriesIncutBlocksUids(newStoriesIncutBlocksUids);

                addingBlocksCache.set(updatedBlock?.uid, updatedBlock);

                setStoriesIncutBlocks((prevState) => [
                    updatedBlock,
                    ...prevState.map((block, index) => ({
                        ...block,
                        position: index + 1,
                    })),
                ]);

                setBlocksChanged(true);
                setIsSwapItems(true);
                enqueueSnackbar(TOP_INCUT_CONTENT_CONSTS.blockWasSavedMsg, { variant: 'success' });
            }
        } catch (e) {
            console.error(e);
        }
    }, [
        currentPublication,
        figureVersion,
        enqueueSnackbar,
        storiesIncutBlocksUids,
        url,
        addingBlocksCache,
        saveStoriesIncutBlock,
        setStoriesIncutBlocksUids,
    ]);

    const handleDeleteBlock = useCallback(
        (blockUid: string) => {
            deletingBlocksCache.add(blockUid);
            setStoriesIncutBlocks((prev) => prev.filter((block) => block.uid !== blockUid));
            setBlocksChanged(true);
            setIsSwapItems(true);

            const newStoriesIncutBlocksUids = storiesIncutBlocksUids || [];
            const updNewStoriesIncutBlocksUids = newStoriesIncutBlocksUids.filter((uid) => uid !== blockUid);
            setStoriesIncutBlocksUids(updNewStoriesIncutBlocksUids);
        },
        [setStoriesIncutBlocks, deletingBlocksCache, storiesIncutBlocksUids, setStoriesIncutBlocksUids]
    );

    useEffect(() => {
        setStoriesIncutBlocksUids(storiesIncutBlocks.map((b) => b.uid));
        if (storiesIncutBlocks.find((item) => !item.figureVersion)) {
            setIsValid(false);
            return;
        }
        setIsValid(true);
    }, [storiesIncutBlocks, setIsValid, setStoriesIncutBlocksUids]);

    const cancelChanges = useCallback(() => {
        resetCache();
        setShowModal(false);
        setConfirmSaveModalOpen(false);
        setStoriesIncutBlocks(storiesIncutStore.storiesIncutBlocks);
        if (nextLocation) {
            history.push(nextLocation);
        }
        if (nextTab !== null && setTabValue) {
            setTabValue(nextTab); // Переключаем вкладку
        }
        setNextTab(null);
    }, [resetCache, setConfirmSaveModalOpen, storiesIncutStore, history, nextLocation, nextTab, setTabValue]);

    const approveChanges = useCallback(async () => {
        await handleUpdateStoriesIncutSelection();
        setShowModal(false);
        setConfirmSaveModalOpen(false);
        if (nextLocation) {
            history.push(nextLocation);
        }
        if (nextTab !== null && setTabValue) {
            setTabValue(nextTab); // Переключаем вкладку
        }
        setNextTab(null);
    }, [handleUpdateStoriesIncutSelection, setConfirmSaveModalOpen, history, nextLocation, nextTab, setTabValue]);

    useEffect(() => {
        if (!isValid) {
            setIsActiveBtn(false);
            return;
        }
        if (isSwapItems || addingBlocksCache.size > 0 || deletingBlocksCache.size > 0) {
            setIsActiveBtn(true);
            return;
        }
        setIsActiveBtn(false);
    }, [addingBlocksCache, deletingBlocksCache, isValid, isSwapItems]);

    const handlePromptNavigation = useCallback(
        (location: { pathname: string }) => {
            const isEditPage = location.pathname.match(new RegExp(`/story/${storyId}/incut/edit/[^/]+`));
            if (isEditPage) {
                return true;
            }

            if (addingBlocksCache.size > 0 || deletingBlocksCache.size > 0) {
                setNextLocation(location.pathname);
                setShowModal(true);
                return false;
            }

            return true;
        },
        [addingBlocksCache, deletingBlocksCache, storyId]
    );

    return (
        <Wrapper>
            {!isReadOnly && (
                <Header>
                    <MaterialIncutUrl
                        blockCount={storiesIncutBlocks.length}
                        setUrl={setUrl}
                        handleAddMaterial={handleCreateBlockModal}
                        setDataLoaded={setDataLoaded}
                        dataLoaded={dataLoaded}
                    />
                    <HistoryAndSaveBox>
                        <ButtonForInput variant="contained" disabled={!isActiveBtn} onClick={handleUpdateStoriesIncutSelection}>
                            {TOP_INCUT_CONTENT_CONSTS.saveMsg}
                        </ButtonForInput>
                    </HistoryAndSaveBox>
                </Header>
            )}
            {storiesIncutBlocks.length === 0 ? (
                <Box color={grey50} marginTop="24px">
                    <Typography variant="text18R">{TOP_INCUT_CONTENT_CONSTS.withoutIncutsTitle}</Typography>
                    {!isReadOnly && (
                        <Box marginTop="8px">
                            <Typography variant="text16R">{TOP_INCUT_CONTENT_CONSTS.withoutIncutsDescription}</Typography>
                        </Box>
                    )}
                </Box>
            ) : (
                <StoryIncutGridDrop
                    storiesIncutBlocks={storiesIncutBlocks}
                    setStoriesIncutBlocks={setStoriesIncutBlocks}
                    handleDeleteBlock={handleDeleteBlock}
                    onSwapItems={setIsSwapItems}
                    storyId={storyId}
                    isReadOnly={isReadOnly}
                />
            )}
            <Prompt message={handlePromptNavigation} />
            <SimpleModal
                mode="dialog"
                open={showModal || confirmSaveModalOpen}
                title={TOP_INCUT_CONTENT_CONSTS.warning}
                onClose={() => {
                    setShowModal(false);
                    setConfirmSaveModalOpen(false);
                    setNextTab(null);
                }}
                onNo={cancelChanges}
                onYes={approveChanges}
                yesLabel={TOP_INCUT_CONTENT_CONSTS.saveMsg}
                noLabel={TOP_INCUT_CONTENT_CONSTS.cancelChangesConfirmation}
            >
                <Box textAlign="center">
                    <ModalDescription variant="h2">{TOP_INCUT_CONTENT_CONSTS.approveSaveChangesQuestion}</ModalDescription>
                </Box>
            </SimpleModal>
        </Wrapper>
    );
};

export default observer(StoryIncutContent);